/**
 * EmployeeSupportTickets.jsx
 *
 * Composant de gestion des tickets côté EMPLOYÉ (support / staff / superadmin).
 * Utilisé dans EmployeeDashboard.jsx pour le tab "tickets".
 *
 * Fonctionnalités exclusives employé vs utilisateur simple :
 *  ✦ Voir TOUS les tickets (pas seulement les siens)
 *  ✦ Notes internes (invisibles pour l'utilisateur)
 *  ✦ Changer le statut (open → in_progress → resolved → closed)
 *  ✦ S'auto-assigner un ticket
 *  ✦ Voir les infos de l'utilisateur dans le panneau latéral
 *  ✦ Filtre par assigné / non assigné
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import {
  empGetTickets, empGetTicket, empUpdateTicket,
  empPostMessage, empSelfAssign,
} from "./employeeClient";

/* ─── Animations ──────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const popIn   = keyframes`0%{transform:scale(.9);opacity:0}100%{transform:scale(1);opacity:1}`;

/* ─── Shared styled components ────────────────────────────────────────────── */
const Panel      = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;overflow:hidden;margin-bottom:1rem;`;
const PHead      = styled.div`display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;padding:.85rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};`;
const PTitle     = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const PActions   = styled.div`display:flex;gap:.5rem;align-items:center;flex-wrap:wrap;`;
const Search     = styled.input`background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};border-radius:7px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};font-size:.75rem;padding:.35rem .75rem;outline:none;width:170px;&:focus{border-color:${({theme})=>theme.borderLight};}&::placeholder{color:${({theme})=>theme.textMuted};}`;
const FilterChip = styled.button`font-family:${({theme})=>theme.fontMono};font-size:.62rem;padding:.22rem .6rem;border-radius:100px;letter-spacing:.05em;cursor:pointer;transition:all .15s;border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};background:${({$active,theme})=>$active?theme.bgHover:"transparent"};color:${({$active,theme})=>$active?theme.text:theme.textMuted};&:hover{border-color:${({theme})=>theme.borderLight};}`;
const THead      = styled.div`display:grid;grid-template-columns:${({$cols})=>$cols};gap:.5rem;padding:.6rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};min-width:640px;`;
const TH         = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const TRow       = styled.div`display:grid;grid-template-columns:${({$cols})=>$cols};gap:.5rem;padding:.85rem 1.25rem;border-bottom:1px solid ${({theme})=>theme.border};align-items:center;transition:background .15s;min-width:640px;cursor:${({$click})=>$click?"pointer":"default"};&:last-child{border-bottom:none;}&:hover{background:${({theme,$click})=>$click?theme.bgHover:"inherit"};}`;
const Cell       = styled.div`font-size:.78rem;color:${({$dim,theme})=>$dim?theme.textDim:theme.text};font-family:${({$mono,theme})=>$mono?theme.fontMono:"inherit"};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const Pill       = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.18rem .55rem;border-radius:100px;letter-spacing:.05em;background:${({$s,theme})=>$s==="open"?"rgba(0,230,118,.1)":$s==="in_progress"?"rgba(255,193,7,.1)":$s==="resolved"?"rgba(79,195,247,.1)":$s==="closed"?theme.bgHover:$s==="urgent"?theme.redDim:$s==="high"?"rgba(255,152,0,.1)":theme.bgHover};color:${({$s,theme})=>$s==="open"?theme.green:$s==="in_progress"?theme.gold:$s==="resolved"?theme.accent:$s==="closed"?theme.textMuted:$s==="urgent"?theme.red:$s==="high"?"#ff9800":theme.textMuted};`;
const Btn        = styled.button`font-family:${({theme})=>theme.fontMono};font-size:.63rem;padding:.25rem .65rem;border-radius:5px;cursor:pointer;letter-spacing:.04em;transition:all .15s;white-space:nowrap;border:1px solid ${({$t,theme})=>$t==="ok"?theme.green+"55":$t==="warn"?theme.gold+"55":$t==="danger"?theme.red+"55":theme.borderLight};background:${({$t,theme})=>$t==="ok"?theme.greenDim:$t==="warn"?"rgba(255,193,7,.08)":$t==="danger"?theme.redDim:theme.bgHover};color:${({$t,theme})=>$t==="ok"?theme.green:$t==="warn"?theme.gold:$t==="danger"?theme.red:theme.textDim};&:disabled{opacity:.35;cursor:not-allowed;}&:hover:not(:disabled){opacity:.8;}`;
const BtnRow     = styled.div`display:flex;gap:.35rem;`;
const Skel       = styled.div`height:48px;margin:.3rem 1.25rem;border-radius:6px;background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);background-size:400px 100%;animation:${shimmer} 1.4s infinite;`;
const Empty      = styled.div`text-align:center;padding:2.5rem;font-size:.8rem;color:${({theme})=>theme.textMuted};`;
const ScrollWrap = styled.div`overflow-x:auto;-webkit-overflow-scrolling:touch;`;
const PriorityDot= styled.div`width:8px;height:8px;border-radius:50%;flex-shrink:0;background:${({$p})=>$p==="urgent"?"#ff3b5c":$p==="high"?"#ff9800":$p==="medium"?"#4fc3f7":"#2a3340"};`;
const Toast      = styled.div`position:fixed;bottom:1.5rem;right:1.5rem;z-index:9999;background:${({theme})=>theme.bgCard};border:1px solid ${({$ok,theme})=>$ok?theme.green+"55":theme.red+"55"};border-radius:10px;padding:.75rem 1rem;color:${({$ok,theme})=>$ok?theme.green:theme.red};font-size:.78rem;box-shadow:0 6px 24px rgba(0,0,0,.4);animation:${popIn} .2s ease;`;

/* ─── Employee-only styles ────────────────────────────────────────────────── */
const EmployeeBadge = styled.div`
  display:inline-flex;align-items:center;gap:.35rem;
  padding:.25rem .7rem;border-radius:6px;
  background:rgba(79,195,247,.08);border:1px solid rgba(79,195,247,.2);
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  color:${({theme})=>theme.accent};letter-spacing:.06em;margin-bottom:.75rem;
`;
const InternalToggle = styled.label`
  display:flex;align-items:center;gap:.45rem;font-size:.72rem;
  color:${({theme})=>theme.textMuted};cursor:pointer;
  input{accent-color:${({theme})=>theme.gold};}
  &:hover{color:${({theme})=>theme.gold};}
`;
const InternalTag = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.55rem;
  padding:.1rem .4rem;border-radius:3px;
  background:rgba(255,193,7,.15);color:#ffc107;
`;
const SupportTag = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.55rem;
  padding:.1rem .4rem;border-radius:3px;
  background:rgba(79,195,247,.15);color:#4fc3f7;
`;
const StatusActions = styled.div`
  display:flex;gap:.4rem;flex-wrap:wrap;
`;
const UserInfoCard = styled.div`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:10px;padding:.9rem 1rem;
`;
const InfoRow = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:.3rem 0;border-bottom:1px solid ${({theme})=>theme.border};
  font-size:.72rem;
  &:last-child{border-bottom:none;}
`;
const InfoKey = styled.span`color:${({theme})=>theme.textMuted};`;
const InfoVal = styled.span`font-family:${({theme})=>theme.fontMono};color:${({theme})=>theme.text};font-size:.7rem;`;

const DetailWrap = styled.div`animation: ${fadeUp} .25s ease;`;
/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function timeAgo(iso) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso)) / 1000;
  if (d < 60)    return "à l'instant";
  if (d < 3600)  return `il y a ${Math.floor(d/60)}m`;
  if (d < 86400) return `il y a ${Math.floor(d/3600)}h`;
  return new Date(iso).toLocaleDateString("fr-FR", {day:"2-digit", month:"short"});
}
function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {day:"2-digit", month:"short", year:"numeric"});
}
function initials(n="") { return n.slice(0,2).toUpperCase(); }

const MOCK_TICKETS = [
  { id:"t1", subject:"Dépôt non crédité depuis 2h",         category:"deposit",    priority:"high",   status:"in_progress", user:"alice",   assigned_to:"Agent Support", message_count:2, created_at:new Date(Date.now()-7200000).toISOString() },
  { id:"t2", subject:"Impossible de retirer mes EVP",       category:"withdrawal", priority:"medium", status:"open",        user:"bob",     assigned_to:null,            message_count:0, created_at:new Date(Date.now()-86400000).toISOString() },
  { id:"t3", subject:"Bug sur la page de trading",            category:"bug",        priority:"low",    status:"resolved",    user:"charlie", assigned_to:"Agent Support", message_count:4, created_at:new Date(Date.now()-172800000).toISOString() },
  { id:"t4", subject:"Vérification d'identité KYC refusée",  category:"kyc",        priority:"urgent", status:"open",        user:"dave",    assigned_to:null,            message_count:0, created_at:new Date(Date.now()-3600000).toISOString() },
];

const STATUS_FLOW = {
  open:        [{val:"in_progress", label:"Prendre en charge", t:"warn"},   {val:"closed", label:"Fermer", t:""}],
  in_progress: [{val:"resolved",    label:"✓ Résoudre",        t:"ok"},    {val:"closed", label:"Fermer", t:""}],
  resolved:    [{val:"closed",      label:"Fermer",             t:""},      {val:"open",   label:"Réouvrir", t:"warn"}],
  closed:      [{val:"open",        label:"Réouvrir",           t:"warn"}],
};

/* ══════════════════════════════════════════════════════════════════════════ */
/* ── Ticket Detail (vue employé) ────────────────────────────────────────── */
/* ══════════════════════════════════════════════════════════════════════════ */
function EmpTicketDetail({ ticketId, empUser, onBack }) {
  const [ticket,   setTicket]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [reply,    setReply]    = useState("");
  const [internal, setInternal] = useState(false);
  const [sending,  setSending]  = useState(false);
  const [acting,   setActing]   = useState(false);
  const [toast,    setToast]    = useState(null);
  const bottomRef = useRef(null);

  const showToast = (msg, ok=true) => {
    setToast({msg, ok});
    setTimeout(()=>setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await empGetTicket(ticketId);
      setTicket(res.data);
    } catch {
      setTicket({
        id:ticketId, subject:"Dépôt non crédité depuis 2h",
        description:"L'utilisateur indique avoir effectué un dépôt EVP de 500$ il y a 2h mais son solde n'a pas été mis à jour. Hash: 0xabc123...",
        category:"deposit", priority:"high", status:"in_progress",
        user:{id:1, username:"alice", email:"alice@exemple.com", date_joined:"2026-01-10", is_active:true},
        assigned_to:"Agent Support",
        created_at:new Date(Date.now()-7200000).toISOString(),
        resolved_at:null,
        messages:[
          {id:1, author:"alice",        is_employee:false, is_internal:false, content:"Bonjour, j'ai fait un dépôt de 500 EVP il y a 2h et mon solde n'a pas changé. Hash: 0xabc123...", created_at:new Date(Date.now()-7200000).toISOString()},
          {id:2, author:"Agent Support",is_employee:true,  is_internal:false, content:"Bonjour Alice, nous avons bien reçu votre demande. Pouvez-vous confirmer le réseau utilisé (Ethereum, Polygon…) ?", created_at:new Date(Date.now()-3600000).toISOString()},
          {id:3, author:"Agent Support",is_employee:true,  is_internal:true,  content:"[Note interne] Vérifier côté wallet provider si la transaction est détectée. TX hash: 0xabc123...", created_at:new Date(Date.now()-3500000).toISOString()},
        ],
      });
    } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(()=>{ load(); }, [load]);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); }, [ticket?.messages?.length]);

  const sendMessage = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    const content = reply.trim();
    try {
      const res = await empPostMessage(ticketId, {content, is_internal:internal});
      setTicket(t=>({...t, messages:[...(t.messages||[]), res.data]}));
    } catch {
      setTicket(t=>({...t, messages:[...(t.messages||[]), {
        id:Date.now(), author:empUser?.username||"Moi",
        is_employee:true, is_internal:internal,
        content, created_at:new Date().toISOString(),
      }]}));
    }
    setReply(""); setSending(false);
    showToast(internal ? "Note interne ajoutée." : "Réponse envoyée à l'utilisateur.");
  };

  const changeStatus = async (s) => {
    setActing(true);
    try { await empUpdateTicket(ticketId, {status:s}); } catch {}
    setTicket(t=>({...t, status:s, resolved_at: s==="resolved"?new Date().toISOString():t.resolved_at}));
    showToast(`Statut mis à jour : ${s}`);
    setActing(false);
  };

  const selfAssign = async () => {
    setActing(true);
    try { await empSelfAssign(ticketId); } catch {}
    setTicket(t=>({...t, assigned_to:empUser?.username||"Moi"}));
    showToast("Ticket assigné à vous.");
    setActing(false);
  };

  if (loading) return (
    <div>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#5a6a7a",fontSize:".8rem",cursor:"pointer",marginBottom:"1.25rem",display:"flex",alignItems:"center",gap:".4rem",padding:0}}>← Retour aux tickets</button>
      <Skel style={{height:"120px",marginBottom:"1rem"}}/><Skel style={{height:"300px"}}/>
    </div>
  );

  if (!ticket) return (
    <div>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#5a6a7a",fontSize:".8rem",cursor:"pointer",marginBottom:"1.25rem",display:"flex",alignItems:"center",gap:".4rem",padding:0}}>← Retour aux tickets</button>
      <p style={{color:"#5a6a7a"}}>Ticket introuvable.</p>
    </div>
  );

  const actions = STATUS_FLOW[ticket.status] || [];

  return (
    <DetailWrap>
      {/* Back + badge employé */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1.25rem"}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"#5a6a7a",fontSize:".8rem",cursor:"pointer",display:"flex",alignItems:"center",gap:".4rem",padding:0}}>
          ← Retour aux tickets
        </button>
        <EmployeeBadge>🎧 VUE EMPLOYÉ — accès étendu</EmployeeBadge>
      </div>

      {/* Header ticket */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:"1.25rem",flexWrap:"wrap",gap:"1rem",background:"#0e1115",border:"1px solid #1e2530",borderRadius:"12px",padding:"1.25rem",borderLeft:`4px solid ${ticket.priority==="urgent"?"#ff3b5c":ticket.priority==="high"?"#ff9800":ticket.priority==="medium"?"#4fc3f7":"#1e2530"}`}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:".6rem",marginBottom:".4rem"}}>
            <PriorityDot $p={ticket.priority}/>
            <h2 style={{fontFamily:"Playfair Display,Georgia,serif",fontSize:"1.15rem",fontWeight:700}}>{ticket.subject}</h2>
          </div>
          <div style={{display:"flex",gap:".4rem",flexWrap:"wrap",alignItems:"center"}}>
            <Pill $s={ticket.status}>{ticket.status}</Pill>
            <Pill $s={ticket.priority}>{ticket.priority}</Pill>
            <span style={{fontSize:".72rem",color:"#5a6a7a",fontFamily:"DM Mono"}}>#{String(ticket.id).slice(0,8)}</span>
            <span style={{fontSize:".72rem",color:"#5a6a7a"}}>📂 {ticket.category}</span>
            {ticket.assigned_to
              ? <span style={{fontSize:".72rem",color:"#4fc3f7"}}>🎧 {ticket.assigned_to}</span>
              : <span style={{fontSize:".72rem",color:"#5a6a7a"}}>Non assigné</span>}
          </div>
        </div>
        {/* Actions de statut */}
        <StatusActions>
          {!ticket.assigned_to && (
            <Btn $t="warn" disabled={acting} onClick={selfAssign}>📌 S'assigner</Btn>
          )}
          {actions.map(a=>(
            <Btn key={a.val} $t={a.t} disabled={acting} onClick={()=>changeStatus(a.val)}>{a.label}</Btn>
          ))}
        </StatusActions>
      </div>

      {/* Layout 2 colonnes */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:"1.25rem"}}>

        {/* Conversation */}
        <Panel>
          <PHead><PTitle>Conversation</PTitle>
            <span style={{fontSize:".65rem",color:"#5a6a7a",fontFamily:"DM Mono"}}>
              {(ticket.messages||[]).filter(m=>!m.is_internal).length} public · {(ticket.messages||[]).filter(m=>m.is_internal).length} interne
            </span>
          </PHead>
          <div style={{padding:"1rem 1.25rem",display:"flex",flexDirection:"column",gap:".75rem",maxHeight:"440px",overflowY:"auto"}}>
            {/* Description */}
            <div style={{background:"rgba(79,195,247,.05)",border:"1px solid rgba(79,195,247,.15)",borderRadius:"8px",padding:".85rem 1rem"}}>
              <div style={{fontSize:".68rem",color:"#4fc3f7",fontWeight:600,marginBottom:".35rem"}}>Description initiale</div>
              <p style={{fontSize:".8rem",lineHeight:1.55,color:"#8a9aaa"}}>{ticket.description}</p>
            </div>
            {/* Messages */}
            {(ticket.messages||[]).map(m=>(
              <div key={m.id} style={{display:"flex",gap:".6rem",alignItems:"flex-start",flexDirection:m.is_employee?"row-reverse":"row"}}>
                <div style={{width:28,height:28,borderRadius:"50%",flexShrink:0,
                  background:m.is_employee?"#1976d2":"#1e2530",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontFamily:"DM Mono",fontSize:".62rem",fontWeight:600}}>
                  {initials(m.author)}
                </div>
                <div style={{
                  flex:1,maxWidth:"85%",
                  background:m.is_internal?"rgba(255,193,7,.06)":m.is_employee?"#141820":"#0e1115",
                  border:`1px solid ${m.is_internal?"rgba(255,193,7,.2)":"#1e2530"}`,
                  borderRadius:m.is_employee?"12px 2px 12px 12px":"2px 12px 12px 12px",
                  padding:".6rem .8rem",
                }}>
                  <div style={{fontSize:".7rem",fontWeight:600,marginBottom:".2rem",
                    color:m.is_employee?"#4fc3f7":"#e8edf2",
                    display:"flex",alignItems:"center",gap:".4rem"}}>
                    {m.author}
                    {m.is_employee && <SupportTag>SUPPORT</SupportTag>}
                    {m.is_internal && <InternalTag>NOTE INTERNE</InternalTag>}
                    <span style={{fontFamily:"DM Mono",fontSize:".58rem",color:"#5a6a7a",marginLeft:"auto"}}>
                      {timeAgo(m.created_at)}
                    </span>
                  </div>
                  <p style={{fontSize:".8rem",lineHeight:1.5}}>{m.content}</p>
                </div>
              </div>
            ))}
            <div ref={bottomRef}/>
          </div>

          {/* Zone de réponse */}
          {ticket.status !== "closed" && (
            <div style={{padding:"1rem 1.25rem",borderTop:"1px solid #1e2530",display:"flex",flexDirection:"column",gap:".6rem",background:"#080a0c"}}>
              <textarea
                placeholder={internal ? "Note interne (invisible pour l'utilisateur)…" : "Votre réponse à l'utilisateur…"}
                value={reply}
                onChange={e=>setReply(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&e.ctrlKey&&sendMessage()}
                style={{
                  width:"100%",background:internal?"rgba(255,193,7,.04)":"#0e1115",
                  border:`1px solid ${internal?"rgba(255,193,7,.3)":"#1e2530"}`,
                  borderRadius:"8px",color:"#e8edf2",fontFamily:"Syne,sans-serif",
                  fontSize:".82rem",padding:".65rem .85rem",outline:"none",
                  resize:"none",minHeight:"80px",transition:"border-color .15s",
                }}
              />
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <InternalToggle>
                  <input type="checkbox" checked={internal} onChange={e=>setInternal(e.target.checked)}/>
                  🔒 Note interne (employés uniquement)
                </InternalToggle>
                <Btn $t={internal?"warn":"ok"} disabled={!reply.trim()||sending} onClick={sendMessage}>
                  {sending?"Envoi…":"Envoyer ↵"}
                </Btn>
              </div>
            </div>
          )}
        </Panel>

        {/* Panneau latéral — infos utilisateur */}
        <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
          <Panel>
            <PHead><PTitle>Utilisateur</PTitle></PHead>
            <div style={{padding:"1rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:".65rem",marginBottom:".85rem"}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:"#1e2530",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"DM Mono",fontSize:".72rem",fontWeight:600}}>
                  {initials(ticket.user?.username||"")}
                </div>
                <div>
                  <div style={{fontSize:".82rem",fontWeight:600}}>{ticket.user?.username}</div>
                  <div style={{fontSize:".7rem",color:"#5a6a7a"}}>{ticket.user?.email}</div>
                </div>
              </div>
              <UserInfoCard>
                <InfoRow><InfoKey>Catégorie</InfoKey><InfoVal>{ticket.category}</InfoVal></InfoRow>
                <InfoRow><InfoKey>Priorité</InfoKey><InfoVal><Pill $s={ticket.priority}>{ticket.priority}</Pill></InfoVal></InfoRow>
                <InfoRow><InfoKey>Créé le</InfoKey><InfoVal>{fmt(ticket.created_at)}</InfoVal></InfoRow>
                <InfoRow><InfoKey>Résolu le</InfoKey><InfoVal>{ticket.resolved_at?fmt(ticket.resolved_at):"—"}</InfoVal></InfoRow>
                <InfoRow><InfoKey>Compte actif</InfoKey><InfoVal style={{color:ticket.user?.is_active?"#00e676":"#ff3b5c"}}>{ticket.user?.is_active?"Oui":"Non"}</InfoVal></InfoRow>
              </UserInfoCard>
            </div>
          </Panel>

          <Panel>
            <PHead><PTitle>Actions rapides</PTitle></PHead>
            <div style={{padding:"1rem",display:"flex",flexDirection:"column",gap:".45rem"}}>
              {!ticket.assigned_to && (
                <Btn $t="warn" style={{width:"100%",justifyContent:"center"}} disabled={acting} onClick={selfAssign}>
                  📌 S'assigner ce ticket
                </Btn>
              )}
              {(STATUS_FLOW[ticket.status]||[]).map(a=>(
                <Btn key={a.val} $t={a.t} style={{width:"100%",justifyContent:"center"}} disabled={acting} onClick={()=>changeStatus(a.val)}>
                  {a.label}
                </Btn>
              ))}
              {ticket.status==="closed" && (
                <div style={{fontSize:".72rem",color:"#5a6a7a",textAlign:"center",padding:".5rem"}}>
                  Ticket fermé
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>

      {toast && <Toast $ok={toast.ok}>{toast.ok?"✓":"✕"} {toast.msg}</Toast>}
    </DetailWrap>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/* ── Ticket List (vue employé) ──────────────────────────────────────────── */
/* ══════════════════════════════════════════════════════════════════════════ */
export default function EmployeeSupportTickets({ empUser, toast }) {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState("ALL");
  const [assign,    setAssign]    = useState("ALL");   // ALL | mine | unassigned
  const [search,    setSearch]    = useState("");
  const [openId,    setOpenId]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "ALL")    params.status = filter;
      if (assign === "mine")        params.assigned_to = "me";
      if (assign === "unassigned")  params.assigned_to = "none";
      const res = await empGetTickets(params);
      setItems(res.data?.results || res.data || []);
    } catch {
      setItems(MOCK_TICKETS);
    } finally { setLoading(false); }
  }, [filter, assign]);

  useEffect(() => { load(); }, [load]);

  const displayed = items.filter(t =>
    !search ||
    t.subject.toLowerCase().includes(search.toLowerCase()) ||
    (t.user||"").toLowerCase().includes(search.toLowerCase())
  );

  const urgentCount   = items.filter(t=>t.priority==="urgent"&&t.status==="open").length;
  const unassignedCount = items.filter(t=>!t.assigned_to&&(t.status==="open")).length;

  // Vue détail
  if (openId) return (
    <EmpTicketDetail
      ticketId={openId}
      empUser={empUser}
      onBack={()=>{ setOpenId(null); load(); }}
    />
  );

  const COLS = "14px 2fr 90px 90px 90px 120px 90px";

  return (
    <>
      {/* Alertes urgentes */}
      {(urgentCount > 0 || unassignedCount > 0) && (
        <div style={{display:"flex",gap:".5rem",marginBottom:"1rem",flexWrap:"wrap"}}>
          {urgentCount > 0 && (
            <div style={{display:"flex",alignItems:"center",gap:".5rem",padding:".5rem .9rem",borderRadius:"8px",background:"rgba(255,59,92,.06)",border:"1px solid rgba(255,59,92,.2)",fontSize:".75rem",color:"#ff3b5c"}}>
              🚨 <strong>{urgentCount}</strong> ticket{urgentCount>1?"s":""} urgent{urgentCount>1?"s":""}
              <button onClick={()=>{setFilter("open");setAssign("ALL");}} style={{background:"none",border:"none",color:"#ff3b5c",cursor:"pointer",textDecoration:"underline",fontSize:".72rem"}}>Voir</button>
            </div>
          )}
          {unassignedCount > 0 && (
            <div style={{display:"flex",alignItems:"center",gap:".5rem",padding:".5rem .9rem",borderRadius:"8px",background:"rgba(255,193,7,.06)",border:"1px solid rgba(255,193,7,.2)",fontSize:".75rem",color:"#ffc107"}}>
              ⏳ <strong>{unassignedCount}</strong> ticket{unassignedCount>1?"s":""} non assigné{unassignedCount>1?"s":""}
              <button onClick={()=>setAssign("unassigned")} style={{background:"none",border:"none",color:"#ffc107",cursor:"pointer",textDecoration:"underline",fontSize:".72rem"}}>Filtrer</button>
            </div>
          )}
        </div>
      )}

      <Panel>
        <PHead>
          <PTitle>
            Tickets support ({items.length})
            <span style={{marginLeft:".5rem",color:"#4fc3f7",fontFamily:"inherit",textTransform:"none",letterSpacing:"normal",fontWeight:400}}>
              — vue employé
            </span>
          </PTitle>
          <PActions>
            <Search placeholder="Sujet, utilisateur…" value={search} onChange={e=>setSearch(e.target.value)}/>
            {["ALL","open","in_progress","resolved","closed"].map(f=>(
              <FilterChip key={f} $active={filter===f} onClick={()=>setFilter(f)}>
                {f==="ALL"?"Tous":f==="in_progress"?"En cours":f==="resolved"?"Résolus":f==="closed"?"Fermés":"Ouverts"}
              </FilterChip>
            ))}
          </PActions>
        </PHead>

        {/* Sous-filtre assignation */}
        <div style={{padding:".5rem 1.25rem",background:"#080a0c",borderBottom:"1px solid #1e2530",display:"flex",gap:".35rem"}}>
          {[
            {val:"ALL",        label:"Tous les tickets"},
            {val:"mine",       label:"Mes tickets"},
            {val:"unassigned", label:"Non assignés"},
          ].map(a=>(
            <FilterChip key={a.val} $active={assign===a.val} onClick={()=>setAssign(a.val)}
              style={{fontSize:".58rem"}}>
              {a.label}
            </FilterChip>
          ))}
        </div>

        <ScrollWrap>
          <THead $cols={COLS}>
            <TH/>
            <TH>Sujet</TH>
            <TH>Catégorie</TH>
            <TH>Priorité</TH>
            <TH>Statut</TH>
            <TH>Utilisateur</TH>
            <TH>Créé</TH>
          </THead>
          {loading
            ? Array.from({length:4}).map((_,i)=><Skel key={i}/>)
            : displayed.length===0
            ? <Empty>Aucun ticket correspondant.</Empty>
            : displayed.map(t=>(
              <TRow key={t.id} $cols={COLS} $click onClick={()=>setOpenId(t.id)}>
                <Cell><PriorityDot $p={t.priority}/></Cell>
                <Cell style={{whiteSpace:"normal",lineHeight:1.3,fontSize:".75rem"}}>
                  {t.subject}
                  {!t.assigned_to && (
                    <span style={{marginLeft:".4rem",fontFamily:"DM Mono",fontSize:".58rem",color:"#ffc107",background:"rgba(255,193,7,.1)",borderRadius:"4px",padding:"0 .35rem"}}>
                      non assigné
                    </span>
                  )}
                </Cell>
                <Cell $dim style={{fontSize:".72rem"}}>{t.category}</Cell>
                <Cell><Pill $s={t.priority}>{t.priority}</Pill></Cell>
                <Cell><Pill $s={t.status}>{t.status}</Pill></Cell>
                <Cell $mono style={{fontSize:".72rem"}}>{t.user}</Cell>
                <Cell $dim style={{fontSize:".7rem"}}>{timeAgo(t.created_at)}</Cell>
              </TRow>
            ))
          }
        </ScrollWrap>
      </Panel>
    </>
  );
}
